import React, { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  ArrowLeft,
  Edit,
  Trash2,
  DollarSign,
  FileText,
  Home,
  Mail,
  Phone,
  Building,
  Calendar,
  Shield,
  // Download,
  CreditCard,
  Activity,
  AlertTriangle
} from 'lucide-react'
import toast from 'react-hot-toast'
import { unifiedAdminApiClient as adminApi } from '../services/unified-admin-api-client'
import { User, CreditAdjustmentForm } from '../types'
import { useAuthStore } from '../stores/authStore'

export default function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const [showCreditModal, setShowCreditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const { checkPermission } = useAuthStore()
  const queryClient = useQueryClient()

  const { data: userData, isLoading, error } = useQuery(
    ['user', userId],
    () => adminApi.getUser(userId!),
    {
      enabled: !!userId,
    }
  )

  const { data: generationsData } = useQuery(
    ['user-generations', userId],
    () => adminApi.getUserGenerations(userId!),
    {
      enabled: !!userId,
    }
  )

  const { data: propertiesData } = useQuery(
    ['user-properties', userId],
    () => adminApi.getUserProperties(userId!),
    {
      enabled: !!userId,
    }
  )

  const deleteUserMutation = useMutation(
    () => adminApi.deleteUser(userId!),
    {
      onSuccess: () => {
        toast.success('User deleted successfully')
        // Redirect to users page
        window.history.back()
      },
      onError: () => {
        toast.error('Failed to delete user')
      },
    }
  )

  const adjustCreditsMutation = useMutation(
    (data: CreditAdjustmentForm) => adminApi.adjustUserCredits(userId!, data),
    {
      onSuccess: () => {
        toast.success('Credits adjusted successfully')
        queryClient.invalidateQueries(['user', userId])
        setShowCreditModal(false)
      },
      onError: () => {
        toast.error('Failed to adjust credits')
      },
    }
  )

  if (isLoading) {
    return <UserDetailSkeleton />
  }

  if (error || !userData?.data) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load user details. Please try again.</p>
      </div>
    )
  }

  const user = userData.data
  const generations = generationsData?.data || []
  const properties = propertiesData?.data || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/users"
            className="text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-6 w-6" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {user.first_name && user.last_name 
                ? `${user.first_name} ${user.last_name}`
                : user.name || 'User Details'
              }
            </h1>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>
        </div>
        
        <div className="flex space-x-3">
          {checkPermission('users:write') && (
            <>
              <button
                onClick={() => setShowCreditModal(true)}
                className="btn-secondary"
              >
                <DollarSign className="h-4 w-4 mr-2" />
                Adjust Credits
              </button>
              <button className="btn-primary">
                <Edit className="h-4 w-4 mr-2" />
                Edit User
              </button>
            </>
          )}
          {checkPermission('users:delete') && (
            <button
              onClick={() => setShowDeleteModal(true)}
              className="btn-danger"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </button>
          )}
        </div>
      </div>

      {/* User Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <UserInfoCard user={user} />
        </div>
        <div>
          <UserStatsCard user={user} generations={generations} properties={properties} />
        </div>
      </div>

      {/* Activity Tabs */}
      <UserActivityTabs 
        user={user} 
        generations={generations} 
        properties={properties} 
      />

      {/* Modals */}
      {showCreditModal && (
        <CreditAdjustmentModal
          user={user}
          onClose={() => setShowCreditModal(false)}
          onSubmit={(data) => adjustCreditsMutation.mutate(data)}
          isLoading={adjustCreditsMutation.isLoading}
        />
      )}

      {showDeleteModal && (
        <DeleteUserModal
          user={user}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => deleteUserMutation.mutate()}
          isLoading={deleteUserMutation.isLoading}
        />
      )}
    </div>
  )
}

interface UserInfoCardProps {
  user: User
}

function UserInfoCard({ user }: UserInfoCardProps) {
  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium">User Information</h3>
      </div>
      <div className="card-body">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Mail className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-900">Email</p>
                <p className="text-sm text-gray-600">{user.email}</p>
              </div>
            </div>
            
            {user.phone && (
              <div className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Phone</p>
                  <p className="text-sm text-gray-600">{user.phone}</p>
                </div>
              </div>
            )}
            
            {user.company && (
              <div className="flex items-center space-x-3">
                <Building className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Company</p>
                  <p className="text-sm text-gray-600">{user.company}</p>
                </div>
              </div>
            )}
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Shield className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-900">User Type</p>
                <span className="badge badge-gray capitalize">{user.user_type}</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Activity className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-900">Status</p>
                <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Calendar className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-900">Joined</p>
                <p className="text-sm text-gray-600">
                  {new Date(user.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Account Verification</p>
              <div className="mt-1 flex items-center space-x-4">
                <span className={`badge ${user.is_verified ? 'badge-success' : 'badge-warning'}`}>
                  Email {user.is_verified ? 'Verified' : 'Unverified'}
                </span>
                <span className={`badge ${user.terms_accepted ? 'badge-success' : 'badge-danger'}`}>
                  Terms {user.terms_accepted ? 'Accepted' : 'Not Accepted'}
                </span>
                <span className={`badge ${user.onboarding_completed ? 'badge-success' : 'badge-warning'}`}>
                  Onboarding {user.onboarding_completed ? 'Complete' : 'Incomplete'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface UserStatsCardProps {
  user: User
  generations: any[]
  properties: any[]
}

function UserStatsCard({ user, generations, properties }: UserStatsCardProps) {
  const totalGenerations = generations.length
  const completedGenerations = generations.filter(g => g.status === 'completed').length
  const failedGenerations = generations.filter(g => g.status === 'failed').length
  
  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium">Statistics</h3>
      </div>
      <div className="card-body">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CreditCard className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-900">Credits</span>
            </div>
            <span className="text-lg font-bold text-primary-600">{user.credits || 0}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FileText className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-900">Generations</span>
            </div>
            <span className="text-lg font-bold text-gray-900">{totalGenerations}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Home className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-900">Properties</span>
            </div>
            <span className="text-lg font-bold text-gray-900">{properties.length}</span>
          </div>
        </div>
        
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Generation Success Rate</h4>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Completed</span>
              <span className="font-medium text-green-600">{completedGenerations}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Failed</span>
              <span className="font-medium text-red-600">{failedGenerations}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Success Rate</span>
              <span className="font-medium">
                {totalGenerations > 0 ? Math.round((completedGenerations / totalGenerations) * 100) : 0}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
interface UserActivityTabsProps {
  user: User
  generations: any[]
  properties: any[]
}

function UserActivityTabs({ user, generations, properties }: UserActivityTabsProps) {
  const [activeTab, setActiveTab] = useState('generations')

  const tabs = [
    { id: 'generations', name: 'Generations', count: generations.length },
    { id: 'properties', name: 'Properties', count: properties.length },
    { id: 'activity', name: 'Activity', count: 0 },
  ]

  return (
    <div className="card">
      <div className="card-header">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
              {tab.count > 0 && (
                <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>
      <div className="card-body">
        {activeTab === 'generations' && <GenerationsTab generations={generations} />}
        {activeTab === 'properties' && <PropertiesTab properties={properties} />}
        {activeTab === 'activity' && <ActivityTab user={user} />}
      </div>
    </div>
  )
}

function GenerationsTab({ generations }: { generations: any[] }) {
  if (generations.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No generations</h3>
        <p className="mt-1 text-sm text-gray-500">This user hasn't created any generations yet.</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Style</th>
            <th>Room Type</th>
            <th>Status</th>
            <th>Credits Used</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {generations.map((generation) => (
            <tr key={generation.id}>
              <td className="font-mono text-sm">{generation.id.slice(0, 8)}...</td>
              <td>{generation.style || 'N/A'}</td>
              <td>{generation.room_type || 'N/A'}</td>
              <td>
                <span className={`badge ${
                  generation.status === 'completed' ? 'badge-success' :
                  generation.status === 'failed' ? 'badge-danger' :
                  generation.status === 'processing' ? 'badge-warning' :
                  'badge-gray'
                }`}>
                  {generation.status}
                </span>
              </td>
              <td>{generation.credits_used}</td>
              <td>
                {generation.created_at && generation.created_at !== '' 
                  ? new Date(generation.created_at).toLocaleDateString() 
                  : 'N/A'
                }
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function PropertiesTab({ properties }: { properties: any[] }) {
  if (properties.length === 0) {
    return (
      <div className="text-center py-12">
        <Home className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No properties</h3>
        <p className="mt-1 text-sm text-gray-500">This user hasn't created any properties yet.</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {properties.map((property) => (
        <div key={property.id} className="border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900">{property.name}</h4>
          {property.address && (
            <p className="text-sm text-gray-500 mt-1">{property.address}</p>
          )}
          <div className="mt-2 flex items-center justify-between">
            <span className="badge badge-gray">{property.property_type || 'Unknown'}</span>
            <span className="text-xs text-gray-500">
              {new Date(property.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

function ActivityTab({ user }: { user: User }) {
  const activities = [
    { id: 1, type: 'login', description: 'User logged in', timestamp: '2024-01-15T10:30:00Z' },
    { id: 2, type: 'generation', description: 'Created generation for modern living room', timestamp: '2024-01-15T09:15:00Z' },
    { id: 3, type: 'property', description: 'Created new property: Downtown Condo', timestamp: '2024-01-14T16:45:00Z' },
  ]

  return (
    <div className="space-y-4">
      {activities.map((activity) => (
        <div key={activity.id} className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="h-2 w-2 bg-blue-500 rounded-full mt-2"></div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-900">{activity.description}</p>
            <p className="text-xs text-gray-500">
              {new Date(activity.timestamp).toLocaleString()}
            </p>
          </div>
        </div>
      ))}
    </div>
  )
}

interface CreditAdjustmentModalProps {
  user: User
  onClose: () => void
  onSubmit: (data: CreditAdjustmentForm) => void
  isLoading: boolean
}

function CreditAdjustmentModal({ user, onClose, onSubmit, isLoading }: CreditAdjustmentModalProps) {
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      amount: parseInt(amount),
      reason,
    })
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Adjust Credits for {user.email}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Amount</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="input"
                placeholder="Enter amount (positive to add, negative to subtract)"
                required
              />
            </div>
            <div>
              <label className="label">Reason</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="input"
                rows={3}
                placeholder="Reason for credit adjustment"
                required
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isLoading || !amount || !reason}
              >
                {isLoading ? 'Adjusting...' : 'Adjust Credits'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

interface DeleteUserModalProps {
  user: User
  onClose: () => void
  onConfirm: () => void
  isLoading: boolean
}

function DeleteUserModal({ user, onClose, onConfirm, isLoading }: DeleteUserModalProps) {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center mb-4">
            <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
            <h3 className="text-lg font-medium text-gray-900">Delete User</h3>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Are you sure you want to delete <strong>{user.email}</strong>? This action cannot be undone.
            All user data, generations, and properties will be permanently deleted.
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="btn-secondary"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="btn-danger"
              disabled={isLoading}
            >
              {isLoading ? 'Deleting...' : 'Delete User'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function UserDetailSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="flex items-center space-x-4">
        <div className="h-6 w-6 bg-gray-200 rounded"></div>
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-body">
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
        <div>
          <div className="card">
            <div className="card-body">
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
